import React, { useState, useCallback, useRef } from 'react';
import { Wrapper, Status } from '@googlemaps/react-wrapper';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  CircularProgress
} from '@mui/material';
import {
  LocationOn,
  Close
} from '@mui/icons-material';

// Coordonnées réelles des villes françaises avec lieux emblématiques
const cityCoordinates = {
  'Paris': { lat: 48.8566, lng: 2.3522 },
  'Lyon': { lat: 45.7640, lng: 4.8357 },
  'Marseille': { lat: 43.2965, lng: 5.3698 },
  'Toulouse': { lat: 43.6047, lng: 1.4442 },
  'Nice': { lat: 43.7102, lng: 7.2620 },
  'Nantes': { lat: 47.2184, lng: -1.5536 },
  'Strasbourg': { lat: 48.5734, lng: 7.7521 },
  'Montpellier': { lat: 43.6110, lng: 3.8767 },
  'Bordeaux': { lat: 44.8378, lng: -0.5792 },
  'Lille': { lat: 50.6292, lng: 3.0573 },
  'Rennes': { lat: 48.1173, lng: -1.6778 },
  'Reims': { lat: 49.2583, lng: 4.0317 },
  'Paris 16ème': { lat: 48.8647, lng: 2.2736 }, // Trocadéro
  'Lyon 3ème': { lat: 45.7578, lng: 4.8320 }, // Part-Dieu
  'Cannes': { lat: 43.5528, lng: 7.0174 },
  'Saint-Tropez': { lat: 43.2677, lng: 6.6407 },
  'Chamonix': { lat: 45.9237, lng: 6.8694 },
  'Biarritz': { lat: 43.4832, lng: -1.5586 },
  'Annecy': { lat: 45.8992, lng: 6.1294 },
  'Avignon': { lat: 43.9493, lng: 4.8059 }
};

// Composant Map Google
const Map = ({ missions, onMissionClick, style }) => {
  const ref = useRef(null);
  const [map, setMap] = useState(null);
  const [selectedMission, setSelectedMission] = useState(null);
  const [infoWindow, setInfoWindow] = useState(null);
  const [markers, setMarkers] = useState([]);

  const initMap = useCallback(() => {
    if (ref.current && !map) {
      const newMap = new window.google.maps.Map(ref.current, {
        center: { lat: 46.6034, lng: 1.8883 }, // Centre de la France
        zoom: 6,
        styles: [
          {
            featureType: 'poi',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }]
          },
          {
            featureType: 'road',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }]
          },
          {
            featureType: 'administrative',
            elementType: 'labels',
            stylers: [{ visibility: 'on' }]
          }
        ],
        mapTypeControl: true,
        streetViewControl: true,
        fullscreenControl: true,
        zoomControl: true
      });
      
      setMap(newMap);
      
      // Créer InfoWindow
      const newInfoWindow = new window.google.maps.InfoWindow();
      setInfoWindow(newInfoWindow);
    }
  }, [map]);

  // Créer les marqueurs pour les missions
  React.useEffect(() => {
    if (map && missions.length > 0) {
      // Nettoyer les anciens marqueurs
      markers.forEach(marker => marker.setMap(null));
      
      const newMarkers = missions.map((mission, index) => {
        const coordinates = cityCoordinates[mission.location] || { lat: 46.6034, lng: 1.8883 };
        
        const marker = new window.google.maps.Marker({
          position: coordinates,
          map: map,
          title: mission.title,
          icon: {
            url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
              <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="12" fill="#4caf50" stroke="white" stroke-width="3"/>
                <text x="16" y="20" text-anchor="middle" fill="white" font-size="12" font-weight="bold">${index + 1}</text>
              </svg>
            `),
            scaledSize: new window.google.maps.Size(32, 32),
            anchor: new window.google.maps.Point(16, 16)
          }
        });
        
        marker.addListener('click', () => {
          setSelectedMission(mission);
          
          const content = `
            <div style="max-width: 250px; padding: 8px;">
              <h3 style="margin: 0 0 8px 0; color: #1976d2;">${mission.title}</h3>
              <p style="margin: 0 0 8px 0; color: #666;">${mission.organization?.name || mission.organization}</p>
              <div style="margin-bottom: 8px;">
                <span style="background: #e3f2fd; color: #1976d2; padding: 2px 8px; border-radius: 12px; font-size: 12px; margin-right: 4px;">${mission.discipline}</span>
                <span style="background: #f5f5f5; color: #666; padding: 2px 8px; border-radius: 12px; font-size: 12px;">📍 ${mission.location}</span>
              </div>
              <p style="margin: 0; font-weight: bold; color: #4caf50;">${mission.rate}</p>
            </div>
          `;
          
          infoWindow.setContent(content);
          infoWindow.open(map, marker);
          
          if (onMissionClick) {
            onMissionClick(mission);
          }
        });
        
        return marker;
      });
      
      setMarkers(newMarkers);
      
      // Ajuster la vue pour inclure tous les marqueurs
      if (newMarkers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        newMarkers.forEach(marker => {
          bounds.extend(marker.getPosition());
        });
        map.fitBounds(bounds);
        
        // Éviter un zoom trop important pour une seule mission
        if (newMarkers.length === 1) {
          map.setZoom(12);
        }
      }
    }
  }, [map, missions, infoWindow, onMissionClick]);

  React.useEffect(() => {
    initMap();
  }, [initMap]);

  return (
    <>
      <div ref={ref} style={style} />
      {selectedMission && (
        <Card sx={{ position: 'absolute', bottom: 16, left: 16, maxWidth: 300, zIndex: 10 }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
              <Box flex={1}>
                <Typography variant="h6" gutterBottom>
                  {selectedMission.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {selectedMission.organization?.name || selectedMission.organization}
                </Typography>
                <Box display="flex" gap={1} mb={2}>
                  <Chip label={selectedMission.discipline} size="small" color="primary" />
                  <Chip label={selectedMission.location} size="small" icon={<LocationOn />} />
                </Box>
                <Typography variant="h6" color="primary" gutterBottom>
                  {selectedMission.rate}
                </Typography>
                <Button 
                  variant="contained" 
                  size="small"
                  onClick={() => {
                    if (onMissionClick) {
                      onMissionClick(selectedMission);
                    }
                  }}
                >
                  Voir détails
                </Button>
              </Box>
              <IconButton size="small" onClick={() => setSelectedMission(null)}>
                <Close />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}
    </>
  );
};

// Carte de fallback simulée
const FallbackMap = ({ missions, onMissionClick, style }) => {
  const [selectedMission, setSelectedMission] = useState(null);
  
  const handleMarkerClick = (mission) => {
    setSelectedMission(mission);
  };
  
  const handleMissionSelect = (mission) => {
    if (onMissionClick) {
      onMissionClick(mission);
    }
    setSelectedMission(null);
  };
  
  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', background: '#e3f2fd', borderRadius: 2, ...style }}>
      <svg width="100%" height="100%" viewBox="0 0 800 400">
        {/* Background */}
        <rect width="800" height="400" fill="#e3f2fd" />
        
        {/* Mock France outline */}
        <path
          d="M200 100 L300 80 L400 90 L500 100 L580 120 L600 180 L580 250 L500 280 L400 290 L300 280 L200 250 L180 180 Z"
          fill="#f5f5f5"
          stroke="#ddd"
          strokeWidth="2"
        />
        
        {/* Mission markers */}
        {missions.map((mission, index) => {
          const coordinates = cityCoordinates[mission.location] || { lat: 46.6034, lng: 1.8883 };
          const x = 200 + (coordinates.lng + 5) * 50;
          const y = 100 + (50 - coordinates.lat) * 4;
          
          return (
            <g key={mission.id}>
              <circle
                cx={x}
                cy={y}
                r={selectedMission?.id === mission.id ? 12 : 8}
                fill={selectedMission?.id === mission.id ? '#1976d2' : '#4caf50'}
                stroke="white"
                strokeWidth="2"
                style={{ cursor: 'pointer' }}
                onClick={() => handleMarkerClick(mission)}
              />
              <text
                x={x}
                y={y + 4}
                textAnchor="middle"
                fill="white"
                fontSize="10"
                fontWeight="bold"
                style={{ pointerEvents: 'none' }}
              >
                {index + 1}
              </text>
            </g>
          );
        })}
      </svg>
      
      {/* Mission info popup */}
      {selectedMission && (
        <Card sx={{ position: 'absolute', bottom: 16, left: 16, maxWidth: 300, zIndex: 10 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {selectedMission.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {selectedMission.organization?.name || selectedMission.organization}
            </Typography>
            <Box display="flex" gap={1} mb={2}>
              <Chip label={selectedMission.discipline} size="small" color="primary" />
              <Chip label={selectedMission.location} size="small" icon={<LocationOn />} />
            </Box>
            <Typography variant="h6" color="primary" gutterBottom>
              {selectedMission.rate}
            </Typography>
            <Box display="flex" gap={1}>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => handleMissionSelect(selectedMission)}
              >
                Voir détails
              </Button>
              <IconButton size="small" onClick={() => setSelectedMission(null)}>
                <Close />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      )}
      
      {/* Info message */}
      <Box sx={{ position: 'absolute', top: 16, right: 16, bgcolor: 'rgba(255,255,255,0.9)', p: 1, borderRadius: 1 }}>
        <Typography variant="caption" color="text.secondary">
          Carte simulée - Configurez Google Maps pour la vraie carte
        </Typography>
      </Box>
    </Box>
  );
};

// Composant de rendu conditionnel pour Google Maps
const render = (status) => {
  switch (status) {
    case Status.LOADING:
      return (
        <Box 
          display="flex" 
          justifyContent="center" 
          alignItems="center" 
          height="400px"
          bgcolor="#f5f5f5"
          borderRadius={2}
        >
          <CircularProgress />
          <Typography variant="body2" sx={{ ml: 2 }}>
            Chargement de la carte...
          </Typography>
        </Box>
      );
    case Status.FAILURE:
      return null; // Retourne null pour utiliser le fallback
    default:
      return null;
  }
};

// Composant principal GoogleMap
const GoogleMap = ({ missions = [], onMissionClick, style }) => {
  // Clé API Google Maps (à remplacer par votre vraie clé)
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'YOUR_GOOGLE_MAPS_API_KEY';
  const [useGoogleMaps, setUseGoogleMaps] = useState(true);
  
  // Si pas de clé API valide ou si Google Maps a échoué, utiliser la carte de fallback
  if (!apiKey || apiKey === 'YOUR_GOOGLE_MAPS_API_KEY' || !useGoogleMaps) {
    return (
      <FallbackMap 
        missions={missions} 
        onMissionClick={onMissionClick}
        style={style || { width: '100%', height: '400px' }}
      />
    );
  }
  
  return (
    <Wrapper 
      apiKey={apiKey} 
      render={(status) => {
        if (status === Status.FAILURE) {
          // En cas d'échec, basculer vers la carte de fallback
          setUseGoogleMaps(false);
          return (
            <FallbackMap 
              missions={missions} 
              onMissionClick={onMissionClick}
              style={style || { width: '100%', height: '400px' }}
            />
          );
        }
        return render(status);
      }} 
      libraries={['places']}
    >
      <Map 
        missions={missions} 
        onMissionClick={onMissionClick}
        style={style || { width: '100%', height: '400px' }}
      />
    </Wrapper>
  );
};

export default GoogleMap;